import React, { createContext, useContext, useEffect, useRef, useCallback, useMemo, useState } from "react";
import { toast } from "sonner";
import { BACKEND_URL } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { logError } from "@/lib/log";

const RealtimeContext = createContext(null);

export function RealtimeProvider({ children }) {
  const { user } = useAuth();
  const handlers = useRef(new Set());
  const wsRef = useRef(null);

  const subscribe = useCallback((fn) => {
    handlers.current.add(fn);
    return () => handlers.current.delete(fn);
  }, []);

  const dispatch = useCallback((data) => {
    handlers.current.forEach((h) => {
      try {
        h(data);
      } catch (e) {
        logError("RealtimeContext", e);
      }
    });
  }, []);

  const send = useCallback((obj) => {
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify(obj));
      return true;
    }
    return false;
  }, []);

  const [connected, setConnected] = useState(false);

  useEffect(() => {
    if (!user) {
      const ws = wsRef.current;
      wsRef.current = null;
      if (ws && ws.readyState === WebSocket.CONNECTING) {
        ws.onopen = () => ws.close();
        ws.onclose = null;
      } else {
        ws?.close();
      }
      return;
    }
    let closed = false;
    let pingTimer = null;
    const url = `${BACKEND_URL.replace(/^http/, "ws")}/api/ws`;

    const connect = () => {
      if (closed) return;
      const ws = new WebSocket(url);
      wsRef.current = ws;

      ws.onopen = () => {
        setConnected(true);
        pingTimer = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify({ type: "ping" }));
        }, 25000);
      };
      ws.onmessage = (e) => {
        let data;
        try {
          data = JSON.parse(e.data);
        } catch {
          return;
        }
        let prefs = { enabled: true, friendRequests: true, messages: true, calls: true, socialActivity: true };
        try {
          prefs = { ...prefs, ...JSON.parse(localStorage.getItem("yaprio-notification-settings") || "{}") };
        } catch {}

        if (data.type === "friend_request") {
          if (prefs.enabled && prefs.friendRequests) {
            toast(`@${data.from} sent you a friend request`);
          }
        } else if (data.type === "follow_request") {
          if (prefs.enabled && prefs.socialActivity) {
            toast(`@${data.from} requested to follow you`);
          }
        } else if (data.type === "follow_accepted") {
          if (prefs.enabled && prefs.socialActivity) {
            toast.success(`@${data.by} accepted your follow request`);
          }
        } else if (data.type === "anonymous-question:new") {
          if (prefs.enabled && prefs.socialActivity) {
            toast("You received an anonymous question");
          }
        } else if (data.type === "friend_accepted") {
          if (prefs.enabled && prefs.friendRequests) {
            toast.success(`@${data.by} accepted your friend request`);
          }
        } else if (data.type === "moderation:you_are_banned") {
          window.dispatchEvent(
            new CustomEvent("yaprio:banned", {
              detail: { code: "banned", reason: data.reason, expires_at: data.expires_at },
            })
          );
        } else if (data.type === "auth:username_changed" || data.type === "auth:role_changed") {
          window.dispatchEvent(new Event("yaprio:refresh-me"));
        } else if (data.type === "moderation:public_chat_timeout") {
          toast.warning("You've been muted from public chat", { description: data.reason || "" });
          window.dispatchEvent(new Event("yaprio:refresh-timeout"));
        } else if (data.type === "moderation:public_chat_timeout_lifted") {
          toast.success("Your public-chat timeout has ended");
          window.dispatchEvent(new Event("yaprio:refresh-timeout"));
        }
        dispatch(data);
      };
      ws.onclose = () => {
        clearInterval(pingTimer);
        setConnected(false);
        if (!closed) setTimeout(connect, 3000);
      };
      ws.onerror = () => ws.close();
    };

    connect();
    return () => {
      closed = true;
      clearInterval(pingTimer);
      const ws = wsRef.current;
      wsRef.current = null;
      if (!ws) return;
      // Closing a socket that is still CONNECTING logs a browser warning, so wait for open.
      if (ws.readyState === WebSocket.CONNECTING) {
        ws.onopen = () => ws.close();
        ws.onclose = null;
        ws.onerror = null;
      } else {
        ws.close();
      }
    };
  }, [user, dispatch]);

  const value = useMemo(() => ({ subscribe, send, connected }), [subscribe, send, connected]);

  return <RealtimeContext.Provider value={value}>{children}</RealtimeContext.Provider>;
}

export function useRealtime() {
  return useContext(RealtimeContext) || { subscribe: () => () => {}, send: () => false, connected: false };
}